import { useEffect, useState } from "react";
import { Modal, Table, Typography } from "antd";
import { getApi } from "redux/apis";
import { toastText } from "utils/utils";
import dayjs from "dayjs";

const { Text } = Typography;

interface IncomingRow {
  skuRaw: string;
  qty: number;
  edaToFtz: string | null;
}

interface Props {
  isOpen: boolean;
  sku: string;
  onClose: () => void;
}

const IncomingDetailModal = ({ isOpen, sku, onClose }: Props) => {
  const [rows, setRows] = useState<IncomingRow[]>([]);
  const [totalQty, setTotalQty] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sku) {
      setRows([]);
      setTotalQty(0);
      fetchDetails();
    }
  }, [isOpen, sku]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await getApi(`/incoming-inventory/details/${encodeURIComponent(sku)}`);
      const data = response?.data?.data;
      setRows(data?.rows || []);
      setTotalQty(data?.totalQty || 0);
    } catch (error: any) {
      toastText(
        error?.response?.data?.message || "Failed to fetch incoming inventory details",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "SKU #",
      dataIndex: "skuRaw",
      key: "skuRaw",
    },
    {
      title: "QTY",
      dataIndex: "qty",
      key: "qty",
    },
    {
      title: "Estimated Arrival Date",
      dataIndex: "edaToFtz",
      key: "edaToFtz",
      render: (val: string | null) =>
        val ? dayjs(val).format("MM/DD/YYYY") : "—",
    },
  ];

  return (
    <Modal
      title={`Incoming Inventory Details - ${sku}`}
      open={isOpen}
      onCancel={onClose}
      centered
      footer={null}
      width={600}
      destroyOnClose
      maskClosable={false}
    >
      <Table
        dataSource={rows}
        columns={columns}
        loading={loading}
        rowKey={(_, index) => String(index)}
        pagination={false}
        size="small"
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Text strong>Total</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <Text strong>{totalQty}</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} />
          </Table.Summary.Row>
        )}
      />
    </Modal>
  );
};

export default IncomingDetailModal;
